import * as utils from "../../utils";
import Timer from "../../../common/timer";

import { connect } from 'react-redux';
import React, { PropTypes, Component } from 'react';
import { fromJS } from 'immutable';
import DraftJS from 'draft-js';
import Editor from './editor';
import Entry from './mention-editor-entry';
import createMentionPlugin, { defaultSuggestionsFilter } from 'draft-js-mention-plugin';
import mentionStrategy from 'draft-js-mention-plugin/mentionStrategy';
import getTypeByTrigger from 'draft-js-mention-plugin/utils/getTypeByTrigger';
import positionSuggestions from 'draft-js-mention-plugin/utils/positionSuggestions';
import urls from 'urls';

const { EditorState, ContentState, Modifier, Entity } = DraftJS;
const findMentionEntities = mentionStrategy('@');

const ENTER_KEY = 'enter';
const SUPPORTS_GET_SELECTION = 'getSelection' in window;
const SUPPORTS_GET_CONTENT_EDITABLE = 'contentEditable' in document.documentElement;

const cachedMentionsMap = new Map();
const memoizeMentionsMap = new Map([['', []]]);
const userByName = {};
const allUserNamesSortedByLength = [];
const tagBody = '(?:[^"\'>]|"[^"]*"|\'[^\']*\')*';
const tagOrComment = new RegExp(
  [
    '<(?:',
    // Comment body.
    '!--(?:(?:-*[^->])*--+|-?)',
    // Special "raw text" elements whose content should be elided.
    `|script\\b${tagBody}>[\\s\\S]*?</script\\s*`,
    `|style\\b${tagBody}>[\\s\\S]*?</style\\s*`,
    // Regular name
    `|/?[a-z]${tagBody}`,
    ')>'
  ].join(''),
  'gi'
);

class MentionEditorBase extends Component {
  static get propTypes() {
    return {
      onSubmit: PropTypes.func.isRequired,
      placeholder: PropTypes.string,
      singleLineInput: PropTypes.bool,
      onFocus: PropTypes.func,
      onBlur: PropTypes.func,
      initialContent: PropTypes.string,
      initialMentions: PropTypes.array,
      autoFocus: PropTypes.bool,
      publicRef: PropTypes.func,
      suggestionsOnTop: PropTypes.bool,
    };
  };

  constructor(props) {
    super(props);
    this.handleReturn = this.handleReturn.bind(this);
    this.focus = _.debounce(this.focus, 100, { leading: true }).bind(this);
    this.blur = this.blur.bind(this);
    this.disableSingleLineInput = this.disableSingleLineInput.bind(this);
    this.enableSingleLineInput = this.enableSingleLineInput.bind(this);

    this.state = {};
    this.className = `editor__${utils.guid()}`;

    this.mentionPluginConfig = {
      entityMutability: 'IMMUTABLE',
      mentionPrefix: '',
      mentionTrigger: '@',
      mentionRegExp: /[^@\s]*/.source
    };

    if (props.suggestionsOnTop) {
      this.mentionPluginConfig.positionSuggestions = this.positionSuggestionsOnTop
    }

    const mentionPlugin = createMentionPlugin(this.mentionPluginConfig);
    this.MentionSuggestions = mentionPlugin.MentionSuggestions;
    this.plugins = [mentionPlugin];

    if (this.props.publicRef) {
      this.props.publicRef(this);
    }
  }

  positionSuggestionsOnTop({ decoratorRect, prevProps, prevState, props, state, popover }) {
    let positions = positionSuggestions({
      decoratorRect,
      popover,
      state,
      props
    });
    const popoverHeight = popover.offsetHeight;
    const inputHeight = popover.parentElement.offsetHeight;
    const defaultPositionTop = parseInt(positions.top.replace('px', ''));
    const popoverPositionTop = (popoverHeight + inputHeight - defaultPositionTop) * -1;

    positions.top = `${popoverPositionTop}px`;

    return positions;
  }

  componentWillUnmount() {
    if (this.props.publicRef) {
      this.props.publicRef(null);
    }
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      this.focus();
    }
  }

  clearComment() {
    this.setComment('');
  }

  blur() {
    this.androidKeyDownTimer && this.androidKeyDownTimer.stop();

    this.setState({ hasFocus: false });

    if (this.props.onBlur) {
      return this.props.onBlur();
    }
  }

  focus() {
    this.androidKeyDownTimer && this.androidKeyDownTimer.restart();

    this.setState({ hasFocus: true });

    if (this.editor) {
      this.editor.focus();
    }

    if (this.props.onFocus) {
      return this.props.onFocus();
    }
  }

  disableSingleLineInput() {
    this.preventSingleLineInput = true;
  }

  enableSingleLineInput() {
    this.preventSingleLineInput = false;
  }

  handleReturn(event) {
    event.preventDefault();

    const metaKey = utils.isWindows() && event.ctrlKey || event.metaKey;
    if (metaKey || this.props.singleLineInput && !this.preventSingleLineInput) {
      // Submit if METAKEY is held with ENTER or if we are a single line input
      this.triggerSubmit(event);
      return 'handled';
    }

    return 'not-handled';
  }

  triggerSubmit(event) {
    this.props.onSubmit(event);
    evite.requestIdleCallback(() => {
      this.editor.blur();
      this.clearComment();
    });
  }

  removeTags(html) {
    var oldHtml;
    do {
      oldHtml = html;
      html = html.replace(tagOrComment, '');
    } while(html !== oldHtml);

    return html.replace(/</g, '&lt;');
  }
}

export class MentionEditorContentEditable extends MentionEditorBase {
  constructor(props) {
    super(props);

    Object.assign(this.state, {
      editorState: EditorState.createEmpty(),
      suggestions: fromJS([]),
      hasContent: false,
      hasFocus: false
    });

    this.handleClick = this.handleClick.bind(this);
    this.handleOpenMentionSuggestions = this.handleOpenMentionSuggestions.bind(this);
    this.handleCloseMentionSuggestions = this.handleCloseMentionSuggestions.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleAddMentionMentionSuggestions = this.handleAddMentionMentionSuggestions.bind(this);
    this.mapResponseToUserDataIteratee = this.mapResponseToUserDataIteratee.bind(this);

    if (evite.detects.isAndroid) {
      // TODO - Remove timer after android browser properly fires event content editable or fixed in draft-js lib
      // Android doesn't fire a change event for content editable except for non char characters, the fix is to check via timer.
      this.handleAndroidTimerTick = this.handleAndroidTimerTick.bind(this);
      this.androidKeyDownTimer = new Timer(100, Number.POSITIVE_INFINITY);
    }
  }

  render() {
    const { state, MentionSuggestions, className, props } = this;
    const { placeholder, focusPlaceholder, singleLineInput } = props;
    const { hasFocus, hasContent } = state;
    const autoFill = 'off';

    let classList = ['mention-editor'];
    if (className) {
      classList.push(className);
    }

    if (hasFocus) {
      classList.push('has-focus');
    }

    if (hasContent) {
      classList.push('has-content');
    }

    if (singleLineInput) {
      classList.push('single-line-input');
    }

    const defaultPlaceholder = 'Write a comment...';
    const defaultFocusPlaceholder = evite.features.isEnabled('mentions')?
      'Use @ to mention a guest.':
    placeholder || defaultPlaceholder;

    const placeholderText = hasFocus?
    focusPlaceholder || defaultFocusPlaceholder:
    placeholder || defaultPlaceholder;

    return (
      <div
        className={classList.join(' ')}
        onClick={this.handleClick}
        onBlur={this.blur}
        onFocus={this.focus}
      >
        <Editor
          autoComplete={autoFill}
          autoCorrect={autoFill}
          autoCapitalize={autoFill}
          spellCheck={autoFill === 'on'}
          placeholder={placeholderText}
          editorState={this.state.editorState}
          onChange={this.handleChange}
          handleReturn={this.handleReturn}
          plugins={this.plugins}
          ref={(element) => {
            this.editor = element;
            if (this.props.editorRef) {
              this.props.editorRef(element);
            }
          }}
        />
        { evite.features.isEnabled('mentions') &&
        <MentionSuggestions
          entryComponent={Entry}
          onOpen={this.handleOpenMentionSuggestions}
          onClose={this.handleCloseMentionSuggestions}
          onAddMention={this.handleAddMentionMentionSuggestions}
          onSearchChange={this.handleSearchChange}
          suggestions={this.state.suggestions}
        />
        }
      </div>
    );
  }

  focus() {
    var editorElement = this.contentEditableElement;

    if (!this.state.hasFocus) {
      if (editorElement) {
        editorElement.setAttribute('contentEditable', 'false');
        editorElement.setAttribute('contentEditable', 'true');
      }
      super.focus();
    }
  }

  handleAndroidTimerTick() {
    const editorElement = this.editor && this.editor.editor.refs.editor;
    if (!editorElement) {
      return;
    }
    const currentHtml = editorElement.innerHTML;
    const currentText = $(currentHtml).find('[data-text]').text();
    if (this.currentText !== currentText) {
      this.currentText = currentText;

      const match = currentText.match(/(?:^|\s)@([^@\s]*)/);
      if (match) {
        this.handleSearchChange({ value: match[1] });
      }
    }
  }

  handleClick() {
    this.setState({ hasFocus: true });

    if (!this.props.singleLineInput) {
      this.focus();
    }
    else if (this.props.onFocus) {
      return this.props.onFocus();
    }
  }

  handleOpenMentionSuggestions() {
    this.disableSingleLineInput();
    $('body').addClass('mention-suggestions-open');

  }

  handleCloseMentionSuggestions() {
    this.enableSingleLineInput();
    $('body').removeClass('mention-suggestions-open');
  }

  handleAddMentionMentionSuggestions() {
    this.editor.restoreEditorDOM();
  }

  componentWillMount() {
    this.androidKeyDownTimer && evite.listenTo(this.androidKeyDownTimer, Timer.TICK, this.handleAndroidTimerTick);

    const { initialContent, initialMentions } = this.props;
    const content = initialContent || '';
    if (content || this.props.autoFocus) {

      const contentState = ContentState.createFromText(content);
      let editorState = EditorState.push(this.state.editorState, contentState);

      if (!initialMentions || !initialMentions.length) {
        this.setState({ editorState: this.moveSelectionToEnd(editorState) });
      } else {
        initialMentions.forEach((mentionData) => {
          const [start, end] = mentionData.start_end;

          editorState = this.insertMention({
            start,
            end,
            editorState,
            name: content.slice(start, end),
          });

          this.setState({ editorState })
        });

        this.setState({ editorState: this.moveSelectionToEnd(editorState) });
      }
    }
  }

  componentDidMount() {
    super.componentDidMount();

    if (this.props.autoFocus) {
      this.forceSelectionToEnd = true;
    }
  }

  componentWillUnmount() {
    let { androidKeyDownTimer }  = this;
    if (androidKeyDownTimer) {
      androidKeyDownTimer.stop();
      evite.stopListening(androidKeyDownTimer, Timer.TICK, this.handleAndroidTimerTick);
    }

    super.componentWillUnmount();
  }

  moveSelectionToEnd(editorState = null) {
    editorState = editorState || EditorState.push(this.state.editorState, ContentState.createFromText(this.comment));

    const text = editorState.getCurrentContent().getPlainText();
    const currentSelectionState = editorState.getSelection();
    const mentionTextSelection = currentSelectionState.merge({
      anchorOffset: text.length,
      focusOffset: text.length
    });

    var contentState = Modifier.insertText(editorState.getCurrentContent(), mentionTextSelection, '', editorState.getCurrentInlineStyle(), null);
    var newEditorState = EditorState.push(editorState, contentState, 'insert-characters');
    return EditorState.forceSelection(newEditorState, contentState.getSelectionAfter());
  }

  appendMentionById(id) {
    this.fetchUserById(id).then((user) => {
      let { comment } = this;

      const name = user.get('name');

      let nextComment = `@${name}`;
      this.setComment(nextComment);
      let contentState = ContentState.createFromText(nextComment);
      let editorState = EditorState.push(this.state.editorState, contentState);

      let start = comment.length;
      let end = start + name.length + 1;

      this.handleChange(this.insertMention({ editorState, name, start, end }));
    });
  }

  insertMention({ editorState, name, start, end }) {
    const { mentionPrefix, mentionTrigger, entityMutability } = this.mentionPluginConfig;
    const mention = new Map([['name', name]]);
    const entityKey = Entity.create(getTypeByTrigger(mentionTrigger), entityMutability, { mention: mention });
    const currentSelectionState = editorState.getSelection();

    const mentionTextSelection = currentSelectionState.merge({
      anchorOffset: start,
      focusOffset: end
    });

    let mentionReplacedContent = Modifier.replaceText(
      editorState.getCurrentContent(),
      mentionTextSelection,
      '' + mentionPrefix + mention.get('name'),
      null,
      entityKey
    );

    const blockKey = mentionTextSelection.getAnchorKey();
    const blockSize = editorState.getCurrentContent().getBlockForKey(blockKey).getLength();
    if (blockSize === end) {
      mentionReplacedContent = Modifier.insertText(mentionReplacedContent, mentionReplacedContent.getSelectionAfter(), ' ');
    }

    var newEditorState = EditorState.push(editorState, mentionReplacedContent, 'insert-mention');
    return EditorState.forceSelection(newEditorState, mentionReplacedContent.getSelectionAfter());
  }

  handleChange(editorState) {
    const currentContent = editorState && editorState.getCurrentContent();
    let comment = currentContent && currentContent.getPlainText() || '';
    const hasContent = comment.length > 0;

    if (this.forceSelectionToEnd && hasContent) {
      this.setState({
        hasContent,
        editorState: this.moveSelectionToEnd(editorState)
      });
    }
    else {
      this.setState({ hasContent, editorState });
    }

    this.forceSelectionToEnd = false;
  }


  handleSearchChange({ value }) {
    const mentionText = value;

    this.fetchUserGuestUsers().then((guestUsers) => {
      let previousSuggestions = this.state.suggestions;
      previousSuggestions = previousSuggestions && previousSuggestions.toArray() || [];

      let newSuggestions = defaultSuggestionsFilter(mentionText, guestUsers, 7);
      if (!_.isEqual(newSuggestions.toArray(), previousSuggestions)) {
        this.setState({
          suggestions: newSuggestions,
        });
      }
    });
  }

  fetchUsers() {
    const eventId = window.event_id;
    const cachedUsers = cachedMentionsMap.get(eventId);

    if (cachedUsers instanceof Array) {
      return Promise.resolve(fromJS(cachedUsers.slice(0)));
    }

    if (cachedUsers instanceof Promise) {
      return cachedUsers
    }

    cachedMentionsMap.set(eventId, new Promise((resolve, reject) => {
      // make ajax request with mentionText then render the list
      const url = this.convertMentionToGuestSearchUrl('');

      $.get(url, (response) => {
          let users = response.map(this.mapResponseToUserDataIteratee);
          const newAllUsers = _(allUserNamesSortedByLength)
            .uniq()
            .sortBy('length')
            .value();

          allUserNamesSortedByLength.splice(0, allUserNamesSortedByLength.length);
          allUserNamesSortedByLength.push.apply(allUserNamesSortedByLength, newAllUsers);

          cachedMentionsMap.set(eventId, users);
          resolve(fromJS(users.slice(0)));
        })
        .fail(function (jqXHR, textStatus, errorThrown) {
          utils.renderAjaxErrorModal(jqXHR, textStatus, errorThrown);
        });
    }));

    return cachedMentionsMap.get(eventId);
  }

  mapResponseToUserDataIteratee(user, index) {
    const avatars = this.props.avatars.userAvatars;
    const userId = user.user_id;
    const firstName = user.primary_name || '';
    const lastName = user.second_name || '';
    const currentAvatar = avatars[userId];

    evite.assert(user.user_id, 'mapResponseToUserDataIteratee: Expected users without an id to not be included in list.');

    const avatar = currentAvatar && currentAvatar.url || '';
    const iconId = currentAvatar && currentAvatar.icon_id || '0';
    const trailingWhitespace = /\s*$/;

    const name = (firstName + ' ' + lastName).replace(trailingWhitespace, '');
    const userData = {
      avatar, firstName, iconId, lastName, name, userId,
      link: `/profile/public/${userId}`,
    };

    allUserNamesSortedByLength.push(name);
    userByName[name] = user;

    return userData
  }

  fetchUserById(id) {
    evite.assert(id, 'fetchUserById: No user id was supplied.');
    return new Promise((resolve, reject) => {
      this.fetchUsers()
        .then((users) => {
          users.map(function (user) {
            if (user.get('userId') === id) {
              resolve(user)
            }
          });

          reject(`Unable to find user by id: ${id}`);
        })
        .catch(function ({ jqXHR, textStatus, errorThrown }) {
          reject(errorThrown);
        });
    });
  }

  fetchUserGuestUsers() {
    return new Promise((resolve, reject) => {
      this.fetchUsers().then((users) => {
          const guestUsers = [];
          users.map(function (user) {
            const isGuest = window.user_id !== user.get('userId');
            if (isGuest) {
              guestUsers.push(user);
            }
          });
          resolve(fromJS(guestUsers));
        })
        .catch(function ({ jqXHR, textStatus, errorThrown }) {
          reject(errorThrown);
        });
    });
  }

  convertMentionToGuestSearchUrl(mentionText) {
    return urls.get('ajax_event_guestlist_search', {
      search_term: mentionText,
      event_id: window.evite_event.id,
      gid: window.guest_id
    });
  }

  setComment(value) {
    this.setState(() => {
      const editorState = EditorState.push(this.state.editorState, ContentState.createFromText(value));
      return { editorState };
    });
  }

  get contentEditableElement() {
    const editor = this.editor;
    return (
      editor &&
      editor.editor &&
      editor.editor.refs &&
      editor.editor.refs.editor ||
      null
    );
  }


  get comment() {
    const editorState = this.editor && this.editor.getEditorState();
    const currentContent = editorState && editorState.getCurrentContent();
    let comment = currentContent && currentContent.getPlainText() || '';

    return this.removeTags(comment);
  }

  get mentions() {
    const editorState = this.editor && this.editor.getEditorState();
    const currentContent = editorState && editorState.getCurrentContent();
    let comment = currentContent && currentContent.getPlainText() || '';
    let mentions = memoizeMentionsMap.get(comment);

    if (mentions) {
      return mentions.slice(0);
    }

    mentions = [];

    const blockMap = currentContent && currentContent.getBlockMap() || [];

    const leftWhitespaceLength = (comment.match(/^\s+/) || []).join('').length;
    const lineOffset = 1;
    let charOffset = 0;
    blockMap.map((block) => {
      if (currentContent) {
        findMentionEntities(block, (start, end) => {
          const mentionStart = start + charOffset;
          const mentionEnd = end + charOffset;

          const mentionName = comment.slice(mentionStart, mentionEnd);
          const user = userByName[mentionName];
          mentions.push({
            user_id: user.user_id,
            start_end: [mentionStart - leftWhitespaceLength, mentionEnd - leftWhitespaceLength]
          });
        }, currentContent);
      }
      charOffset += block.getText().length + lineOffset;
    });

    mentions = mentions.sort(this.sortByMetionStartIteratee);
    memoizeMentionsMap.set(comment, mentions);
    return mentions;
  }

  sortByMetionStartIteratee(mention1, mention2) {
    const metionStart1 = mention1.start_end[0];
    const metionStart2 = mention2.start_end[0];

    if (metionStart1 < metionStart2) {
      return -1;
    }

    return 1;
  }
}

export class DisabledMentionEditor extends MentionEditorBase {
  constructor(props) {
    super(props);
    this.handleKeyDownOnInput = this.handleKeyDownOnInput.bind(this);
  }

  render() {
    const { state, className, props } = this;
    const { placeholder, focusPlaceholder, singleLineInput } = props;
    const { hasFocus, hasContent, comment } = state;

    let classList = ['mention-editor'];
    if (className) {
      classList.push(className);
    }

    if (hasFocus) {
      classList.push('has-focus');
    }

    if (hasContent) {
      classList.push('has-content');
    }

    if (singleLineInput) {
      classList.push('single-line-input');
    }

    const defaultPlaceholder = 'Write a comment...';
    const defaultFocusPlaceholder = placeholder || defaultPlaceholder;

    const placeholderText = hasFocus?
    focusPlaceholder || defaultFocusPlaceholder:
    placeholder || defaultPlaceholder;
    return (
      <div className={classList.join(' ')}>
        <textarea
          className='mention-input'
          placeholder={placeholderText}
          tabIndex="1"
          ref={(c) => this.editor = c}
          onKeyDown={this.handleKeyDownOnInput }
          onBlur={this.blur}
          onFocus={this.focus}
        >{comment}</textarea>
      </div>
    );
  }

  componentWillMount() {
    const { initialContent, autoFocus } = this.props;
    const comment = initialContent || '';
    if (comment || this.props.autoFocus) {
      this.setState({ comment });
    }
  }

  handleKeyDownOnInput(event) {
    if (event.key.toLowerCase() === ENTER_KEY) {
      return this.handleReturn(event);
    }
  }

  setComment(value) {
    this.editor.value = value;
    this.setState({ comment: value || '' });
  }

  get comment() {
    return this.removeTags(this.editor && String(this.editor.value) || '');
  }

  get mentions() {
    return [];
  }
}

let MentionEditor;
if (!evite.features.isEnabled('draftjs') || !SUPPORTS_GET_SELECTION || !SUPPORTS_GET_CONTENT_EDITABLE) {
  MentionEditor = DisabledMentionEditor;
}
else {
  function mapStateToProps(state) {
    return {
      avatars: state.avatars
    }
  }

  MentionEditor = connect(mapStateToProps)(MentionEditorContentEditable);
}

export default MentionEditor;



// WEBPACK FOOTER //
// ./assets/es6/view_invitation/common/components/mention-editor.es6